import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { registerUnauthorizedHandler } from '../lib/api/client'
import type { Session } from '../types/domain'

const STORAGE_KEY = 'nexo_session'

interface AuthContextValue {
  session: Session | null
  isAdmin: boolean
  setSession: (session: Session | null) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function readStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<Session | null>(() => readStoredSession())

  const setSession = useCallback((next: Session | null) => {
    setSessionState(next)
    if (next) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  const logout = useCallback(() => setSession(null), [setSession])

  useEffect(() => {
    registerUnauthorizedHandler(() => setSession(null))
  }, [setSession])

  const isAdmin = Boolean(
    session && (session.role === 'admin' || (session.perms ?? '').split(',').includes('manage_users')),
  )

  return <AuthContext.Provider value={{ session, isAdmin, setSession, logout }}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  return ctx
}
